import React from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Currency.module.scss';
import Button from '../../../../components/button/button';
import FormNewCheckbox from '../../../../components/formCheckbox/FormNewCheckbox';
import FormDropdown from '../../../../components/formDropdown/FormDropdown';
import FormInput from '../../../../components/formInput/FormInput';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import InputContainer from '../../../../components/template/InputContainer/InputContainer';
import Notes from '../../../../components/template/Notes/Notes';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface CurrencyProps {}

const Currency: React.FC<CurrencyProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext();
  const {
    formState: { isValid },
    getValues,
    watch,
    reset,
  } = formMethods;
  const { currency } = watch();
  //#endregion

  const CurrencyData = {
    title: 'Currency risk',
    description:
      'The losses that an international financial transaction may incur due to currency fluctuations.',
    compulsoryQuestions: [
      {
        id: 'currency.compulsory',
        question: 'Is your Company exposed to currency risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'currency.probability',
        question: 'How frequent is the Company facing currency risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
  };

  const currencyOptions = [
    {
      label: 'USD',
      value: 'usd',
    },
    {
      label: 'EUR',
      value: 'eur',
    },
    {
      label: 'AUD',
      value: 'aud',
    },
    {
      label: 'CNY',
      value: 'cny',
    },
    {
      label: 'GBP',
      value: 'gbp',
    },
  ];

  const expectationOptions = [
    {
      label: 'Yes, even above my expectation',
      value: 1,
    },
    {
      label: 'Yes, within my expectation',
      value: 2,
    },
    {
      label: 'Neither above nor below my expectation',
      value: 3,
    },
    {
      label: 'No, below my expectation',
      value: 4,
    },
    {
      label: 'Not sure / I don’t know',
      value: 5,
    },
  ];

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = CurrencyData.compulsoryQuestions.map((comp) => {
      return {
        answer: getValues('currency.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      };
    });

    compulsoryQuestions.push(
      ...([
        {
          answer: getValues('currency.currency'),
          question: 'Currency',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('currency.buyOrSell'),
          question: 'Buy or Sell',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('currency.priceRate'),
          question: 'Fill in Exchange Price Rate',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('currency.tradeSize'),
          question: 'Trade size',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('currency.companyExpectedMargin'),
          question: 'Company expected margin (%)',
          questionNotes: [],
          answerNotes: [],
        },
      ] as any)
    );

    const finalCurrencyData = new QuestionForm(
      CurrencyData.title,
      CurrencyData.description,
      compulsoryQuestions,
      [
        {
          answer:
            expectationOptions.find(
              (item) => item.value == getValues('currency.expectation')
            )?.label || '',
          answerNotes: [],
          marks: +getValues('currency.expectation'),
          question: 'Is the result within your expectation?',
          questionNotes: [],
        },
      ],
      CurrencyData.probabilityQuestions.map((comp) => {
        return {
          answer:
            comp.inputs.find(
              (inp) => inp.value == Number(getValues('currency.probability'))
            )?.label || '',
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
          marks: Number(getValues('currency.probability')),
        };
      })
    );

    setQuestion({ currency: finalCurrencyData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      currency?.compulsory == '' ? undefined : currency?.compulsory == 'Yes'
    );
  };

  const generateRandomData = () => {
    const randomNumber = (min = 1, max = 100000) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: randomNumber(1, 5),
      currency: randomNumber(1, 5),
      buyOrSell: randomNumber(1, 2),
      tradeSize: randomNumber(1, 10000).toString(),
      priceRate: randomNumber(1, 1000).toString(),
      companyExpectedMargin: randomNumber(1, 5),
      expectation: randomNumber(1, 5).toString(),
    };
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="currency"
          title={CurrencyData.title}
          description={CurrencyData.description}
          compulsoryQuestions={CurrencyData.compulsoryQuestions}
          probabilityQuestions={CurrencyData.probabilityQuestions}
        />

        {currency?.compulsory && currency?.compulsory == 'Yes' && (
          <SectionContainer>
            <InputContainer>
              <FormDropdown
                id="currency.currency"
                label="Currency"
                placeholder="Choose the currency"
                options={currencyOptions || []}
                required
              />
              <div>
                <FormDropdown
                  id="currency.buyOrSell"
                  label="Buy or Sell"
                  placeholder="Choose buy or sell"
                  options={[
                    {
                      label: 'Buy',
                      value: 1,
                    },
                    {
                      label: 'Sell',
                      value: 2,
                    },
                  ]}
                  required
                />
              </div>
              <div>
                <FormInput
                  id="currency.priceRate"
                  label="Fill in Exchange Price Rate"
                  placeholder="Enter exchange price rate"
                  type="number"
                  required
                />
              </div>
              <div>
                <FormInput
                  id="currency.tradeSize"
                  label="Trade size"
                  placeholder="Enter trade size"
                  type="number"
                  required
                />
                <Notes>
                  Enter trade size in lots - remember the more the lots you buy,
                  the greater the risks
                </Notes>
              </div>
              <FormInput
                id="currency.companyExpectedMargin"
                label="Company expected margin (%)"
                placeholder="Enter company expected margin"
                type="number"
                required
              />
            </InputContainer>

            <InputContainer>
              <FormDropdown
                id="currency.expectation"
                label="Is the result within your expectation?"
                placeholder="Choose the expectation"
                options={expectationOptions || []}
                required
              />
            </InputContainer>
          </SectionContainer>
        )}
      </div>
      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              currency: {
                ...getValues().currency,
                ...generateRandomData(),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Currency;
